import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import Carousel from 'components/Carousel';
import Container from 'components/Container';
import Link from 'components/Link';
import Stack from 'components/Stack';
import Text from 'components/Text';

const Section = styled.section`
  padding: 4.25rem 0;
  position: relative;
  z-index: 1;
`;

const StyledContainer = styled(Container)`
  max-width: calc(100% - 2rem);
  margin-block-end: 2.625rem;
`;

const StyledCarouselSlides = styled(Carousel.Slides)`
  /* Use negative block margin to prevent clipping of box-shadows */
  margin-bottom: -1.5rem;
  margin-top: -1.5rem;
  padding: 1.5rem 0;
`;

const StyledCard = styled(Card)`
  background-image: linear-gradient(
    rgb(29 41 57 / 60%) 35%,
    rgb(16 24 40 / 60%)
  );
  border: 0.125rem solid #344054;
  border-radius: 0.5rem;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px,
    rgba(0, 0, 0, 0.1) 0px 2px 4px -2px;
  inline-size: 23rem;
  max-inline-size: 75vw;
  padding: 1.5rem;
  position: relative;
  line-height: 1.625;

  h3 {
    font-weight: bold;
    line-height: 1.625;
    text-wrap: balance;
  }

  a {
    text-decoration: none;
  }

  p {
    -webkit-font-smoothing: antialiased;
    line-height: 1.85;
    max-inline-size: 16rem;
    text-wrap: pretty;
  }

  .gatsby-image-wrapper {
    min-block-size: min(10vw, 11rem);
  }
`;

export default function DoraMetricsFeatureSection() {
  const data = useStaticQuery(
    graphql`
      query {
        news: allRecentNewsJson {
          edges {
            node {
              id
              title
              subcategory
              link
              image {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                }
              }
            }
          }
        }
      }
    `
  );

  const items = data.news?.edges.map(({ node }) => node);

  return (
    <Section>
      <Carousel
        alignSlides="stretch"
        gap="1rem"
        justifySlides="start"
        offset="max(1rem, calc((100vw - 75rem) / 2))"
      >
        <StyledContainer as={Stack} gap="2.625rem" size="lg">
          <Text as="h2" variant="heading.section">
            Recent News
          </Text>
          <Text variant="md">
            Read about our most recent company milestones, product releases, and
            more.
          </Text>
        </StyledContainer>
        <StyledCarouselSlides>
          {items.map((item, index) => (
            <Carousel.Slide key={index}>
              <StyledCard {...item} />
            </Carousel.Slide>
          ))}
        </StyledCarouselSlides>
      </Carousel>
    </Section>
  );
}

function Card({ className, image, link, subcategory, title }) {
  return (
    <Stack as="article" gap="1rem" className={className}>
      {image && (
        <GatsbyImage
          image={getImage(image.image?.asset || image)}
          alt={image?.alt || title}
        />
      )}
      <Text as="h3" variant="smallP">
        <Link to={link} variant="fill">
          {subcategory}
        </Link>
      </Text>
      <Text variant="smallestP">{title}</Text>
    </Stack>
  );
}
